/**
 * Parse a cron expression into a human-readable format
 * Supports standard cron format: minute hour day month weekday
 */
export function parseCronExpression(cronExpression: string): string {
  try {
    const parts = cronExpression.trim().split(/\s+/);
    
    if (parts.length < 5) {
      return cronExpression; // Return as-is if invalid
    }

    const [minute, hour, dayOfMonth, month, dayOfWeek] = parts;

    // Common patterns
    if (cronExpression === '* * * * *') return 'Every minute';
    if (cronExpression === '0 * * * *') return 'Every hour';
    if (cronExpression === '0 0 * * *') return 'Daily at midnight';
    if (cronExpression === '0 9 * * *') return 'Daily at 9:00 AM';
    if (cronExpression === '0 0 * * 0') return 'Weekly on Sunday at midnight';
    if (cronExpression === '0 0 1 * *') return 'Monthly on the 1st at midnight';

    // Handle */N patterns (every N minutes/hours)
    if (minute.startsWith('*/') && hour === '*' && dayOfMonth === '*' && month === '*' && dayOfWeek === '*') {
      const interval = parseInt(minute.substring(2));
      if (!isNaN(interval)) {
        return `Every ${interval} minute${interval > 1 ? 's' : ''}`;
      }
    }
    if (minute === '0' && hour.startsWith('*/') && dayOfMonth === '*' && month === '*' && dayOfWeek === '*') {
      const interval = parseInt(hour.substring(2));
      if (!isNaN(interval)) {
        return `Every ${interval} hour${interval > 1 ? 's' : ''}`;
      }
    }

    let description = '';

    // Frequency
    if (minute === '*' && hour === '*' && dayOfMonth === '*' && month === '*' && dayOfWeek === '*') {
      return 'Every minute';
    }

    // Time of day (only for specific times, not intervals)
    if (!minute.startsWith('*/') && !hour.startsWith('*/') && (minute !== '*' || hour !== '*')) {
      const hourNum = hour === '*' ? null : parseInt(hour);
      const minuteNum = minute === '*' ? null : parseInt(minute);

      if (hourNum !== null && minuteNum !== null && !isNaN(hourNum) && !isNaN(minuteNum)) {
        const period = hourNum >= 12 ? 'PM' : 'AM';
        const displayHour = hourNum === 0 ? 12 : hourNum > 12 ? hourNum - 12 : hourNum;
        description = `at ${displayHour}:${minuteNum.toString().padStart(2, '0')} ${period}`;
      } else if (hourNum !== null && !isNaN(hourNum)) {
        description = `at hour ${hourNum}`;
      } else if (minuteNum !== null && !isNaN(minuteNum)) {
        description = `at minute ${minuteNum}`;
      }
    }

    // Day of week
    if (dayOfWeek !== '*') {
      const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
      const dayNum = parseInt(dayOfWeek);
      if (!isNaN(dayNum) && dayNum >= 0 && dayNum <= 6) {
        description = `Every ${days[dayNum]} ${description}`;
      } else if (dayOfWeek.includes(',')) {
        const dayNums = dayOfWeek.split(',').map(d => parseInt(d));
        const dayNames = dayNums.map(d => days[d]).join(', ');
        description = `Every ${dayNames} ${description}`;
      } else if (dayOfWeek.includes('-')) {
        description = `Weekly ${description}`;
      } else {
        description = `Weekly ${description}`;
      }
    }
    // Day of month
    else if (dayOfMonth !== '*') {
      if (dayOfMonth === '1') {
        description = `Monthly on the 1st ${description}`;
      } else if (dayOfMonth === '15') {
        description = `Monthly on the 15th ${description}`;
      } else {
        description = `Monthly on day ${dayOfMonth} ${description}`;
      }
    }
    // Month
    else if (month !== '*') {
      const months = ['January', 'February', 'March', 'April', 'May', 'June', 
                      'July', 'August', 'September', 'October', 'November', 'December'];
      const monthNum = parseInt(month) - 1;
      if (!isNaN(monthNum) && monthNum >= 0 && monthNum <= 11) {
        description = `Yearly in ${months[monthNum]} ${description}`;
      }
    }
    // Default to daily
    else {
      description = `Daily ${description}`;
    }

    return description.trim() || cronExpression;
  } catch (error) {
    return cronExpression; // Return as-is if parsing fails
  }
}

/**
 * Get time until next run
 * @param nextRunAt - Unix timestamp in seconds or ISO string
 */
export function getTimeUntilRun(nextRunAt: number | string): string {
  const now = new Date();
  // Handle both Unix timestamps (numbers) and ISO strings
  const nextRun = typeof nextRunAt === 'number' 
    ? new Date(nextRunAt * 1000) 
    : new Date(nextRunAt);
  const diff = nextRun.getTime() - now.getTime();

  if (diff < 0) {
    return 'Overdue';
  }

  const seconds = Math.floor(diff / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);

  if (days > 0) {
    return `in ${days} day${days > 1 ? 's' : ''}`;
  }
  if (hours > 0) {
    return `in ${hours} hour${hours > 1 ? 's' : ''}`;
  }
  if (minutes > 0) {
    return `in ${minutes} minute${minutes > 1 ? 's' : ''}`;
  }
  return `in ${seconds} second${seconds !== 1 ? 's' : ''}`;
}
